import React from 'react';
import { Button, Table, Typography, theme, type TableColumnsType, type TableProps } from 'antd';
import { ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';

export type PerformanceByPathRow = {
  key: string;
  /** First-level path only, e.g. `/docs/` — deeper URLs roll up into this row. */
  path: string;
  /** Prerender served it to Googlebot AND it has at least one impression. */
  crawledVisible: number;
  /** Prerender served it to Googlebot, no impressions in this window. */
  crawledInvisible: number;
  /** GSC impressions in this window, with no Prerender crawl observed in the same period. */
  notServed: number;
  /** Average CTR across impressioned pages in the path, 0–100. `null` when there are none. */
  ctr: number | null;
  /** Crawled pages that came back non-200 to Googlebot. `null` when no crawl data in the window. */
  errors: number | null;
};

export type PerformanceByPathSortField =
  | 'path'
  | 'crawledVisible'
  | 'crawledInvisible'
  | 'notServed'
  | 'ctr'
  | 'errors';

export type PerformanceByPathSort = {
  field: PerformanceByPathSortField;
  order: 'asc' | 'desc';
};

export type PerformanceByPathTableChange = {
  /** Which interaction produced this change — lets the caller track sorts apart from paging. */
  action: 'sort' | 'paginate';
  /** 1-based page after the interaction. A sort change always lands on page 1. */
  page: number;
  /** `null` is antd's third-click "cancel sort" — the caller applies its default. */
  sort: PerformanceByPathSort | null;
};

export const PERFORMANCE_BY_PATH_PAGE_SIZE = 10;

const PERFORMANCE_BY_PATH_TABLE_STRINGS = {
  columns: {
    path: 'Path',
    crawledVisible: 'Crawled, with impressions',
    crawledInvisible: 'Crawled, no impressions',
    notServed: 'Not Prerender-served, with impressions',
    ctr: 'CTR',
    errors: 'Delivery errors',
    review: 'Review',
  },
  // Load-bearing hedge — SearchPerformance/AGENTS.md: bucket-3 wording is constrained.
  notServedHedge:
    'URLs that appeared in Google search results during the period, with no Prerender crawl observed for them in that same period. It does not mean Prerender never served them — a crawl before this window would look the same.',
  formatReviewAriaLabel: (path: string) => `Review ${path}`,
  formatTotal: (total: number) => `${total.toLocaleString()} paths`,
};

export type PerformanceByPathTableProps = {
  rows: PerformanceByPathRow[];
  loading: boolean;
  /** 1-based current page of the server-side pagination. */
  page: number;
  /** Unpaged row count. */
  total: number;
  sort: PerformanceByPathSort;
  onChange: (change: PerformanceByPathTableChange) => void;
  /** Review action — called with the row's raw section key (`record.key`). */
  onReview: (sectionKey: string) => void;
};

/** Controlled: every column needs `sorter: true` and explicit `sortOrder` (`null` on inactive columns, or antd runs them uncontrolled). */
function PerformanceByPathTable({ rows, loading, page, total, sort, onChange, onReview }: PerformanceByPathTableProps) {
  const { token } = theme.useToken();

  const sortOrderOf = (field: PerformanceByPathSortField) =>
    sort.field === field ? (sort.order === 'asc' ? ('ascend' as const) : ('descend' as const)) : null;

  // Numeric columns cycle descend → ascend → cancel. The table opens sorted
  // descending on one of them, and antd's own ascend-first cycle would step
  // that column straight to "cancel" — which the section maps back to the
  // same default, so the header could never be re-sorted. Path keeps A→Z first.
  const descendingFirst = { sortDirections: ['descend' as const, 'ascend' as const] };

  const columns: TableColumnsType<PerformanceByPathRow> = [
    {
      title: PERFORMANCE_BY_PATH_TABLE_STRINGS.columns.path,
      dataIndex: 'path',
      sorter: true,
      sortOrder: sortOrderOf('path'),
      render: (path: string) => <Typography.Text style={{ color: token.colorPrimary }}>{path}</Typography.Text>,
    },
    {
      title: PERFORMANCE_BY_PATH_TABLE_STRINGS.columns.crawledVisible,
      dataIndex: 'crawledVisible',
      width: 150,
      align: 'right',
      sorter: true,
      ...descendingFirst,
      sortOrder: sortOrderOf('crawledVisible'),
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: PERFORMANCE_BY_PATH_TABLE_STRINGS.columns.crawledInvisible,
      dataIndex: 'crawledInvisible',
      width: 150,
      align: 'right',
      sorter: true,
      ...descendingFirst,
      sortOrder: sortOrderOf('crawledInvisible'),
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: (
        <CustomTooltip placement="top" message={PERFORMANCE_BY_PATH_TABLE_STRINGS.notServedHedge}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.marginXXS, cursor: 'help' }}>
            {PERFORMANCE_BY_PATH_TABLE_STRINGS.columns.notServed}
            <InfoCircleOutlined aria-hidden="true" style={{ fontSize: token.fontSizeSM }} />
          </span>
        </CustomTooltip>
      ),
      dataIndex: 'notServed',
      width: 170,
      align: 'right',
      sorter: true,
      ...descendingFirst,
      sortOrder: sortOrderOf('notServed'),
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: PERFORMANCE_BY_PATH_TABLE_STRINGS.columns.ctr,
      dataIndex: 'ctr',
      width: 112,
      align: 'right',
      sorter: true,
      ...descendingFirst,
      sortOrder: sortOrderOf('ctr'),
      render: (value: number | null) => (value === null ? '—' : `${value}%`),
    },
    {
      title: PERFORMANCE_BY_PATH_TABLE_STRINGS.columns.errors,
      dataIndex: 'errors',
      width: 170,
      align: 'right',
      sorter: true,
      ...descendingFirst,
      sortOrder: sortOrderOf('errors'),
      // Thresholds still open with Lucas (PRE-2633).
      render: (value: number | null) =>
        value === null ? (
          '—'
        ) : (
          <Typography.Text
            style={{
              color: value > 20 ? token.colorError : value > 5 ? token.colorWarning : token.colorTextSecondary,
            }}
          >
            {value.toLocaleString()}
          </Typography.Text>
        ),
    },
    {
      title: PERFORMANCE_BY_PATH_TABLE_STRINGS.columns.review,
      align: 'center',
      width: 80,
      render: (_: unknown, record: PerformanceByPathRow) =>
        record.key === 'uncategorized' ? null : (
          <Button
            type="text"
            size="small"
            icon={<ArrowRightOutlined style={{ color: token.colorPrimary }} />}
            aria-label={PERFORMANCE_BY_PATH_TABLE_STRINGS.formatReviewAriaLabel(record.path)}
            onClick={() => onReview(record.key)}
          />
        ),
    },
  ];

  const handleChange: TableProps<PerformanceByPathRow>['onChange'] = (pagination, _filters, sorter, extra) => {
    if (extra.action === 'sort') {
      const single = Array.isArray(sorter) ? sorter[0] : sorter;
      const nextSort = single?.order
        ? {
            field: single.field as PerformanceByPathSortField,
            order: single.order === 'ascend' ? ('asc' as const) : ('desc' as const),
          }
        : null;
      // antd does not reset a controlled `current` on sort — a new order
      // starts from page 1 or the caller keeps reading a stale offset.
      onChange({ action: 'sort', page: 1, sort: nextSort });
      return;
    }
    if (extra.action === 'paginate') {
      onChange({ action: 'paginate', page: pagination.current ?? 1, sort });
    }
  };

  return (
    <Table<PerformanceByPathRow>
      dataSource={rows}
      columns={columns}
      rowKey="key"
      loading={loading}
      onChange={handleChange}
      pagination={{
        current: page,
        pageSize: PERFORMANCE_BY_PATH_PAGE_SIZE,
        total,
        showSizeChanger: false,
        showTotal: PERFORMANCE_BY_PATH_TABLE_STRINGS.formatTotal,
      }}
      size="middle"
      scroll={{ x: 'max-content' }}
    />
  );
}

export default PerformanceByPathTable;
